import type { Lineup } from '@stanwars/sim';
import { content } from '../content/bundle';

/**
 * Build a runtime `Lineup` (with member + loadout + skills, sufficient for BattleScene playback)
 * from an array of member ids. The skills picked are the first passive / first 2 actives / first
 * ultimate in each member's content pool.
 *
 * @throws if any member id is unknown or doesn't have ≥1 passive, ≥2 actives, ≥1 ultimate.
 */
export function buildLineupFromMemberIds(memberIds: readonly string[], playerId: string): Lineup {
  if (memberIds.length !== 5) {
    throw new Error(`buildLineupFromMemberIds: expected 5 member ids, got ${memberIds.length}`);
  }
  const slots = memberIds.map((memberId, i) => {
    const member = content.getMember(memberId);
    if (!member) throw new Error(`Unknown member: ${memberId}`);
    const skills = content.getSkillsForMember(memberId);
    const passive = skills.find(s => s.type === 'passive');
    const actives = skills.filter(s => s.type === 'active');
    const ult = skills.find(s => s.type === 'ultimate');
    if (!passive || actives.length < 2 || !ult) {
      throw new Error(`Incomplete skills for ${memberId}: need 1 passive + 2 actives + 1 ultimate`);
    }
    return {
      member: {
        id: member.id,
        stageName: member.stageName.parody,
        groupId: member.groupId,
        role: member.role,
        rarity: member.rarity,
        stats: member.stats
      },
      position: i as 0 | 1 | 2 | 3 | 4,
      loadout: {
        passiveId: passive.id,
        activeIds: [actives[0]!.id, actives[1]!.id] as [string, string],
        ultimateId: ult.id
      },
      skills: [passive, actives[0]!, actives[1]!, ult].map(s => ({
        id: s.id,
        ownerMemberId: s.ownerMemberId,
        type: s.type,
        archetypeId: s.archetypeId,
        params: s.params,
        cooldown: s.cooldown,
        flavorName: s.flavorName,
        flavorText: s.flavorText
      }))
    };
  });
  return { playerId, slots: slots as Lineup['slots'] };
}
